import { Box, Col, Row, Text } from '@opendoor/bricks-next';
import type { EntryComponent, IRenderContext } from 'cms/entries/entries';
import type { ILpComponentLwcHeroCard } from 'declarations/contentful';

import { generatePresetPictureSet } from '../shared/performance/pictures';
import type { PresetPictureSet } from '../shared/performance/pictures';
import Grid, { gridStyles } from './Grid';
import styles from './LWCHeroCard.module.css';

// Hardcoded breakpoints from @opendoor/bricks/theme/novo: [0, 375, 768, 1201, 1440]
const PICTURE_SIZES: PresetPictureSet = [
  {
    minWidth: 0,
    maxWidth: 375,
    preset: 'square-256',
  },
  {
    minWidth: 375,
    maxWidth: 768,
    preset: 'square-512',
  },
  {
    minWidth: 768,
    maxWidth: 1201,
    preset: 'square-512',
  },
  {
    minWidth: 1201,
    maxWidth: null,
    preset: 'square-1024',
  },
];

const RenderLWCHeroCard = (
  entry: ILpComponentLwcHeroCard,
  _resolvedData?: unknown,
  context?: IRenderContext,
) => {
  const { fields } = entry;
  const isVideoAsset = fields?.clipImage && fields?.videoUrl;
  const isFirstBlockEntry = Boolean(context?.isFirstBlockEntry);

  const containerClassName = [
    styles.containerMinHeight,
    isVideoAsset ? styles.containerVideoMaxHeight : styles.containerImageMaxHeight,
  ].join(' ');

  return (
    <Col analyticsName={`cosmos-landing-page-lwc-hero-card-${fields?.analyticsId}`} trackImpression>
      <Grid>
        <Box
          className={containerClassName}
          $platform-web={{ gridColumn: gridStyles.colSpan12 }}
          backgroundColor="$backgroundSecondary"
          borderRadius="$16x"
          mb="$10x"
          $largerThanSM={{
            mb: '$12x',
            flexDirection: 'row',
            pl: '$32x',
            gap: '$6x',
            height: '$382x',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
          $largerThanMD={{ pl: '$40x', height: '$616x' }}
          justifyContent="space-between"
          pl="$0x"
          gap="$0x"
          height="$452x"
          $largerThanXS={{ height: '$616x' }}
          flexDirection="column"
          alignItems="center"
          overflow="hidden"
        >
          <Col
            pt="$16x"
            $largerThanSM={{
              pt: '$32x',
              px: '$0x',
              pb: '$32x',
              height: '100%',
              maxWidth: '$310x',
            }}
            $largerThanMD={{ pt: '$40x', px: '$0x', pb: '$40x', maxWidth: '$500x' }}
            px="$16x"
            justifyContent="space-between"
            width="100%"
          >
            <Col>
              <Text
                tag="h3"
                display="block"
                typography="$bodyLarge"
                fontWeight="$medium"
                $largerThanMD={{ typography: '$subheaderMedium' }}
                color={'$contentPrimary'}
                mb="$24x"
              >
                {fields.heroCardName}
              </Text>
            </Col>
            <Col width="100%" $largerThanSM={{ width: '83%' }}>
              <Text
                tag="h3"
                display="block"
                typography="$subheaderMedium"
                $largerThanSM={{ typography: '$headerSmall' }}
                $largerThanMD={{ typography: '$headerMedium' }}
                color={'$contentPrimary'}
              >
                {fields.title}
              </Text>
              {fields.titleAccent && (
                <Text
                  tag="h3"
                  display="block"
                  typography="$subheaderMedium"
                  $largerThanSM={{ typography: '$headerSmall' }}
                  $largerThanMD={{ typography: '$headerMedium' }}
                  color={'$contentStateInactivePrimary'}
                >
                  {fields.titleAccent}
                </Text>
              )}
              {fields.body && (
                <Text
                  tag="h3"
                  display="block"
                  typography="$bodySmall"
                  color={'$contentPrimary'}
                  mt="$10x"
                >
                  {fields.body}
                </Text>
              )}
            </Col>
          </Col>
          <Col width="100%" height="100%" minHeight="$310x" position="relative" flexShrink={1}>
            {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
            {isVideoAsset ? (
              <Row
                className={styles.videoFlexWrapper}
                height="100%"
                width="100%"
                justifyContent="center"
                $largerThanSM={{ justifyContent: 'flex-end', pt: '$24x' }}
                alignItems="flex-end"
              >
                <Row
                  width="100%"
                  justifyContent="center"
                  $largerThanSM={{ justifyContent: 'flex-end', minWidth: '$284x' }}
                  $largerThanXS={{ maxHeight: '$430x' }}
                  $largerThanMD={{ minWidth: '$500x', maxHeight: 9999 }}
                  height="100%"
                  overflow="hidden"
                >
                  <video
                    className={styles.video}
                    height="100%"
                    width="100%"
                    src={fields?.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload={isFirstBlockEntry ? 'auto' : 'metadata'}
                  />
                </Row>
              </Row>
            ) : fields?.cardImage ? (
              <Col
                className={styles.imagePositionWrapper}
                position="absolute"
                width="100%"
                height="100%"
                left="$0"
                bottom="$0"
                $largerThanSM={{ pt: '$12' }}
              >
                {generatePresetPictureSet({
                  photoUrl: fields?.cardImage,
                  sizes: PICTURE_SIZES,
                  debugImageSizes: false,
                  preload: isFirstBlockEntry,
                  lazy: !isFirstBlockEntry,
                  objectFit: 'cover',
                })}
              </Col>
            ) : null}
          </Col>
        </Box>
      </Grid>
    </Col>
  );
};

const LWCHeroCard: EntryComponent<ILpComponentLwcHeroCard> = {
  render: RenderLWCHeroCard,
};

export default LWCHeroCard;
